import {CSSProperties, forwardRef, ReactNode} from 'react'
import {NumericFormatProps} from 'react-number-format'
import {StyledInputText, StyledInputTextGroup, StyledInputWrapper} from '@components/commons/input-text/style.ts'
import {Label} from '@components/ui/label/Label.tsx'
import {InputStatusIcon} from '@components/ui/input-status-icon/InputStatusIcon.tsx'
import {InputHelpText} from '@components/ui/input-help-text/InputHelpText.tsx'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTranslation} from '@/translations/i18n'
import {StyledInputPrice} from './style'

export interface InputPriceProps extends Omit<NumericFormatProps, 'type'> {
    className?: string
    label?: ReactNode
    name?: string
    typeIcon?: ReactNode
    touched?: boolean
    errorMessage?: string
    successMessage?: string
    helpText?: string
    placeholder?: string
    inputSize?: 'sm' | 'md' | 'lg'
    width?: CSSProperties['width']
    isOptional?: boolean
    disabled?: boolean
}

export const InputPrice = forwardRef<HTMLInputElement, InputPriceProps>(
    (
        {
            label,
            name,
            typeIcon,
            touched = false,
            errorMessage,
            successMessage,
            helpText,
            className,
            disabled,
            placeholder,
            inputSize = 'md',
            width = '100%',
            isOptional = false,
            thousandSeparator = '.',
            decimalSeparator = ',',
            decimalScale = 2,
            fixedDecimalScale = true,
            allowNegative = false,
            ...rest
        },
        ref
    ) => {
        const {t} = useTranslation()

        return (
            <StyledInputText className={className} $width={width} direction="column" gap={1.5}>
                {label && (
                    <Label htmlFor={name}>
                        {label} {isOptional && <span className="description">({t('commons:optional')})</span>}
                    </Label>
                )}
                <StyledInputTextGroup align="center" fullWidth $hasError={!!errorMessage} $disabled={disabled}>
                    <StyledInputWrapper hasError={!!errorMessage} $size={inputSize}>
                        {typeIcon}
                        <StyledInputPrice
                            getInputRef={ref}
                            name={name}
                            placeholder={placeholder}
                            disabled={disabled}
                            thousandSeparator={thousandSeparator}
                            decimalSeparator={decimalSeparator}
                            decimalScale={decimalScale}
                            fixedDecimalScale={fixedDecimalScale}
                            allowNegative={allowNegative}
                            {...rest}
                        />
                    </StyledInputWrapper>
                </StyledInputTextGroup>
                {(helpText || errorMessage || successMessage) && (
                    <Flexbox align="center" gap={errorMessage || successMessage ? 1.5 : 0}>
                        {(successMessage || errorMessage) && (
                            <InputStatusIcon touched={touched} hasError={!!errorMessage} />
                        )}
                        <InputHelpText error={errorMessage} success={successMessage} helpText={helpText} />
                    </Flexbox>
                )}
            </StyledInputText>
        )
    }
)

InputPrice.displayName = 'InputPrice'
